import { Fragment, useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Factory,
  Play,
  QrCode,
  ScanLine,
  Sparkles,
  Store,
  Users,
} from "lucide-react";
import { Link } from "react-router";
import { useLanguage } from "../../i18n/language";
import { RewardsBrandLockup, RewardsSiteFooter, RewardsSiteHeader, RewardsSiteLinks } from "./RewardsSiteChrome";
import { ScanDemoScreenshot } from "./RewardsDemoVisuals";
import "./RewardsLandingPage.css";

const ASSET_ROOT = "/marketing/gatiq-rewards";
const roleIcons = [Factory, Users, Store, Users];
const journeyIcons = [QrCode, Users, ScanLine, Check];

export const translations = {
  en: {
    pageTitle: "GatiQ Rewards — Recognition for the people who make it happen",
    languageLabel: "Choose language",
    englishLabel: "English",
    hindiLabel: "हिन्दी",
    skip: "Skip to main content",
    brandLabel: "GatiQ Rewards · Home",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    navLabel: "Main menu",
    nav: { journey: "How it works", platform: "Platform", film: "Watch the film", login: "Platform login" },
    hero: {
      eyebrow: "For the hands that make it happen",
      titleFirst: "Your hard work",
      titleSecond: "deserves to be recognised.",
      description: "Scan the code on an eligible product, see your QCoin in the app, and follow the reward steps set by your company.",
      journeyButton: "See how rewards work",
      whatsappButton: "Talk on WhatsApp",
      proofOne: "Scan a QR code or enter its number",
      proofTwo: "See QCoin and claim updates in the app",
      platformView: "Desktop · GatiQ Rewards",
      demoWorkspace: "Demo workspace",
      screenshotAlt: "Real GatiQ Rewards demo screen showing network, counter, and karigar activity",
      screenshotCaption: "Reward network overview",
      tabletLabel: "Tablet · Performance",
      tabletAlt: "GatiQ Rewards performance dashboard with scan and network summaries",
      mobileLabel: "Mobile · Karigar app",
      mobileAlt: "Karigar mobile app home screen with QCoin balance and scan action",
      batchTitle: "Scan a product code",
      batchNote: "QR or printed number",
      activityTitle: "Your reward journey",
      activityNote: "See QCoin and claim status",
      footerLine: "A simple reward journey for karigars",
      continue: "Continue to the page",
    },
    network: {
      eyebrow: "One reward network",
      titleFirst: "From company to counter.",
      titleSecond: "From counter to karigar.",
      description: "Every role has a clear place. GatiQ Rewards connects the full journey on one platform.",
      roles: [
        { meta: "PROGRAM MANAGER", title: "Company", description: "Issue reward codes and manage the program." },
        { meta: "FIELD NETWORK", title: "Distributor", description: "Extend the network through your field team." },
        { meta: "LOCAL SUPPORT", title: "Counter", description: "Help karigars join and take part." },
        { meta: "REWARD EARNER", title: "Karigar", description: "Scan a code and earn QCoin." },
      ],
      aria: "Reward network from company to distributor, counter, and karigar",
      note: "Reward eligibility and QCoin rules follow each company's program.",
    },
    journey: {
      eyebrow: "Clear steps",
      titleFirst: "From code to claim,",
      titleSecond: "a straightforward journey.",
      sideNote: "The company sets up the network. Counters and karigars join. Scans and claim requests appear in the platform.",
      steps: [
        { title: "Issue reward codes", description: "Issue codes for eligible products and keep a record of each batch.", tag: "COMPANY" },
        { title: "Connect counters and karigars", description: "Bring counters and karigars into your field network.", tag: "NETWORK" },
        { title: "Scan and earn QCoin", description: "Karigars scan a QR code or enter its printed number. QCoin appear in the app.", tag: "KARIGAR" },
        { title: "Send a claim request", description: "Karigars send a claim request in the app; the platform shows its status.", tag: "CLAIM" },
      ],
    },
    platform: {
      eyebrow: "A view of the whole network",
      titleFirst: "The work,",
      titleSecond: "in one place.",
      description: "View code batches, product and counter performance, and share useful product information with your team from the platform.",
      featureGroup: "Choose a platform screen",
      screenLabel: "PLATFORM SCREEN",
      actualUi: "REAL PLATFORM UI",
      features: [
        { title: "Network overview", alt: "GatiQ Rewards platform screen showing a reward network overview", description: "See your reward network and its activity in one overview." },
        { title: "Issue reward codes", alt: "GatiQ Rewards platform screen for issuing a reward code batch", description: "Create reward code batches on the platform." },
        { title: "Track code batches", alt: "GatiQ Rewards platform screen showing reward code batches and their status", description: "Track code batches from issue through scan." },
        { title: "View performance", alt: "GatiQ Rewards platform performance summary screen", description: "Review product and counter performance in one place." },
        { title: "Share information", alt: "GatiQ Rewards platform screen for sharing product information", description: "Share important product information with your network." },
      ],
    },
    film: {
      eyebrow: "See GatiQ Rewards in action",
      titleFirst: "A connected reward network,",
      titleSecond: "from code to claim.",
      description: "Follow the platform journey—from issuing codes to scanning, tracking, and claim requests. The film has Hindi narration and on-screen captions.",
      title: "GatiQ Rewards product film",
      detail: "48 seconds · Hindi narration",
      videoAlt: "48-second GatiQ Rewards product film with Hindi narration",
      disclosure: "Screens shown are from the Zenith Pro Adhesives demo workspace and do not represent a customer account.",
    },
    clarity: {
      eyebrow: "Clear rules, clear journey",
      title: "Each program has its own rules.",
      description: "Product eligibility, QCoin value, and claim terms are set by each company's rewards program. Ask your company representative for details about your program.",
      faqLink: "Read frequently asked questions",
    },
    faq: {
      eyebrow: "Questions?",
      title: "Simple answers.",
      intro: "Talk with our team to learn more about GatiQ Rewards.",
      call: "Call us",
      items: [
        { question: "Who is GatiQ Rewards for?", answer: "It is a rewards platform for companies that run code and reward programs with their counters and karigars. A program may also include distributors and other field roles." },
        { question: "How does a karigar earn QCoin?", answer: "A karigar scans the QR code on an eligible product or enters the printed code number in the app. QCoin from a successful claim appear in the app." },
        { question: "What is each QCoin worth?", answer: "QCoin value and claim terms can vary by company program. Check your program information or ask your company representative or counter." },
        { question: "What if a code will not scan?", answer: "Enter the code number printed below the QR code. If you still need help, ask your counter." },
        { question: "How can my business learn more?", answer: "Call or WhatsApp the GatiQ Rewards team to discuss your network and program." },
      ],
    },
    closing: {
      eyebrow: "Take the next step",
      titleFirst: "Let’s make the reward",
      titleSecond: "journey clearer.",
      description: "Karigars: ask your company about your programme. Companies: talk with us about bringing your network together.",
      call: "Call",
      whatsapp: "WhatsApp",
      note: "GatiQ Rewards · A reward journey for companies, counters, and karigars",
    },
    footer: { tagline: "One reward journey, across your network.", backToTop: "Back to top" },
  },
  hi: {
    "pageTitle": "GatiQ Rewards — मेहनत करने वालों के हुनर की पहचान",
    "languageLabel": "भाषा चुनें",
    "englishLabel": "English",
    "hindiLabel": "हिन्दी",
    "skip": "मुख्य जानकारी पर जाएँ",
    "brandLabel": "GatiQ Rewards — होम",
    "menuOpen": "मेन्यू खोलें",
    "menuClose": "मेन्यू बंद करें",
    "navLabel": "मुख्य मेन्यू",
    "nav": {
      "journey": "कैसे काम करता है",
      "platform": "प्लेटफ़ॉर्म",
      "film": "वीडियो देखें",
      "login": "प्लेटफ़ॉर्म लॉगिन"
    },
    "hero": {
      "eyebrow": "हर हुनरमंद हाथ के लिए",
      "titleFirst": "आपकी मेहनत",
      "titleSecond": "पहचाने जाने के काबिल है।",
      "description": "योग्य प्रोडक्ट का कोड स्कैन करें, ऐप में QCoin देखें और अपनी कंपनी के इनाम कार्यक्रम के अगले कदम जानें।",
      "journeyButton": "इनाम कैसे काम करता है",
      "whatsappButton": "WhatsApp पर बात करें",
      "proofOne": "QR स्कैन करें या छपा नंबर डालें",
      "proofTwo": "ऐप में QCoin और क्लेम की जानकारी देखें",
      "platformView": "डेस्कटॉप · GatiQ Rewards",
      "demoWorkspace": "डेमो अकाउंट",
      "screenshotAlt": "GatiQ Rewards डेमो स्क्रीन पर नेटवर्क, काउंटर और कारीगर की गतिविधि",
      "screenshotCaption": "इनाम नेटवर्क का हाल",
      "tabletLabel": "टैबलेट · प्रदर्शन",
      "tabletAlt": "स्कैन और नेटवर्क सारांश वाला GatiQ Rewards प्रदर्शन डैशबोर्ड",
      "mobileLabel": "मोबाइल · कारीगर ऐप",
      "mobileAlt": "कारीगर ऐप की होम स्क्रीन, QCoin बैलेंस और स्कैन विकल्प के साथ",
      "batchTitle": "प्रोडक्ट कोड स्कैन करें",
      "batchNote": "QR या छपा हुआ नंबर",
      "activityTitle": "आपका इनाम सफ़र",
      "activityNote": "QCoin और क्लेम की जानकारी देखें",
      "footerLine": "कारीगरों के लिए इनाम का आसान सफ़र",
      "continue": "आगे पढ़ें"
    },
    "network": {
      "eyebrow": "एक इनाम नेटवर्क",
      "titleFirst": "कंपनी से काउंटर तक।",
      "titleSecond": "काउंटर से कारीगर तक।",
      "description": "हर व्यक्ति की अपनी भूमिका है। GatiQ Rewards पूरे इनाम सफ़र को एक प्लेटफ़ॉर्म पर जोड़ता है।",
      "roles": [
        {
          "meta": "कार्यक्रम चलाएँ",
          "title": "कंपनी",
          "description": "इनाम कोड जारी करें और कार्यक्रम संभालें।"
        },
        {
          "meta": "फील्ड नेटवर्क",
          "title": "डिस्ट्रीब्यूटर",
          "description": "अपनी टीम के ज़रिए नेटवर्क आगे बढ़ाएँ।"
        },
        {
          "meta": "स्थानीय सहयोग",
          "title": "काउंटर",
          "description": "कारीगरों को जुड़ने और हिस्सा लेने में मदद करें।"
        },
        {
          "meta": "इनाम पाने वाले",
          "title": "कारीगर",
          "description": "कोड स्कैन करें और QCoin कमाएँ।"
        }
      ],
      "aria": "कंपनी से डिस्ट्रीब्यूटर, काउंटर और कारीगर तक इनाम नेटवर्क",
      "note": "इनाम की पात्रता और QCoin के नियम हर कंपनी के कार्यक्रम के अनुसार हैं।"
    },
    "journey": {
      "eyebrow": "सीधे और साफ़ कदम",
      "titleFirst": "कोड से क्लेम तक,",
      "titleSecond": "आसान सफ़र।",
      "sideNote": "कंपनी नेटवर्क तैयार करती है। काउंटर और कारीगर जुड़ते हैं। स्कैन और क्लेम की जानकारी प्लेटफ़ॉर्म पर दिखती है।",
      "steps": [
        {
          "title": "इनाम कोड जारी करें",
          "description": "योग्य प्रोडक्ट के लिए कोड जारी करें और हर बैच का रिकॉर्ड रखें।",
          "tag": "कंपनी"
        },
        {
          "title": "काउंटर और कारीगर जोड़ें",
          "description": "अपने फील्ड नेटवर्क में काउंटर और कारीगरों को शामिल करें।",
          "tag": "नेटवर्क"
        },
        {
          "title": "स्कैन करें, QCoin कमाएँ",
          "description": "कारीगर QR कोड स्कैन करें या छपा नंबर डालें। QCoin ऐप में दिखेंगे।",
          "tag": "कारीगर"
        },
        {
          "title": "क्लेम रिक्वेस्ट भेजें",
          "description": "कारीगर ऐप से क्लेम रिक्वेस्ट भेजें और उसका स्टेटस देखें।",
          "tag": "क्लेम"
        }
      ]
    },
    "platform": {
      "eyebrow": "पूरे नेटवर्क की एक झलक",
      "titleFirst": "काम का हिसाब,",
      "titleSecond": "एक ही जगह।",
      "description": "कोड बैच, प्रोडक्ट और काउंटर का प्रदर्शन देखें। ज़रूरी प्रोडक्ट जानकारी अपने नेटवर्क के साथ साझा करें।",
      "featureGroup": "प्लेटफ़ॉर्म स्क्रीन चुनें",
      "screenLabel": "प्लेटफ़ॉर्म स्क्रीन",
      "actualUi": "असली प्लेटफ़ॉर्म स्क्रीन",
      "features": [
        {
          "title": "नेटवर्क का हाल",
          "alt": "GatiQ Rewards प्लेटफ़ॉर्म पर इनाम नेटवर्क का हाल",
          "description": "अपने इनाम नेटवर्क और उसकी गतिविधि एक जगह देखें।"
        },
        {
          "title": "इनाम कोड जारी करें",
          "alt": "GatiQ Rewards प्लेटफ़ॉर्म पर इनाम कोड बैच जारी करने की स्क्रीन",
          "description": "प्लेटफ़ॉर्म पर इनाम कोड के बैच बनाएँ।"
        },
        {
          "title": "कोड बैच ट्रैक करें",
          "alt": "GatiQ Rewards प्लेटफ़ॉर्म पर कोड बैच और उनका स्टेटस",
          "description": "कोड जारी होने से स्कैन होने तक उनका हाल देखें।"
        },
        {
          "title": "प्रदर्शन देखें",
          "alt": "GatiQ Rewards प्लेटफ़ॉर्म का प्रदर्शन सारांश",
          "description": "प्रोडक्ट और काउंटर का प्रदर्शन एक जगह देखें।"
        },
        {
          "title": "जानकारी साझा करें",
          "alt": "GatiQ Rewards प्लेटफ़ॉर्म पर प्रोडक्ट जानकारी साझा करने की स्क्रीन",
          "description": "ज़रूरी प्रोडक्ट जानकारी अपने नेटवर्क के साथ साझा करें।"
        }
      ]
    },
    "film": {
      "eyebrow": "GatiQ Rewards को काम करते देखें",
      "titleFirst": "जुड़ा हुआ इनाम नेटवर्क,",
      "titleSecond": "कोड से क्लेम तक।",
      "description": "कोड जारी करने से लेकर स्कैन, ट्रैकिंग और क्लेम रिक्वेस्ट तक का सफ़र देखें। इस वीडियो में हिंदी आवाज़ और अंग्रेज़ी कैप्शन हैं।",
      "title": "GatiQ Rewards प्रोडक्ट वीडियो",
      "detail": "48 सेकंड · हिंदी आवाज़",
      "videoAlt": "हिंदी आवाज़ वाला 48 सेकंड का GatiQ Rewards प्रोडक्ट वीडियो",
      "disclosure": "दिखाई गई स्क्रीन Zenith Pro Adhesives डेमो वर्कस्पेस की हैं, किसी ग्राहक के अकाउंट की नहीं।"
    },
    "clarity": {
      "eyebrow": "नियम साफ़, सफ़र साफ़",
      "title": "हर कार्यक्रम के अपने नियम।",
      "description": "कौन-सा प्रोडक्ट योग्य है, QCoin की कीमत और क्लेम की शर्तें कंपनी तय करती है। अपने कार्यक्रम की जानकारी कंपनी प्रतिनिधि से लें।",
      "faqLink": "अक्सर पूछे जाने वाले सवाल पढ़ें"
    },
    "faq": {
      "eyebrow": "कोई सवाल?",
      "title": "आसान जवाब।",
      "intro": "GatiQ Rewards के बारे में जानने के लिए हमारी टीम से बात करें।",
      "call": "कॉल करें",
      "items": [
        {
          "question": "GatiQ Rewards किसके लिए है?",
          "answer": "यह उन कंपनियों का इनाम प्लेटफ़ॉर्म है जो अपने काउंटर और कारीगरों के साथ कोड और इनाम कार्यक्रम चलाती हैं। कार्यक्रम में डिस्ट्रीब्यूटर और फील्ड टीम भी जुड़ सकते हैं।"
        },
        {
          "question": "कारीगर QCoin कैसे कमाते हैं?",
          "answer": "कारीगर योग्य प्रोडक्ट का QR कोड स्कैन करते हैं या ऐप में छपा कोड नंबर डालते हैं। सफल क्लेम के QCoin ऐप में दिखते हैं।"
        },
        {
          "question": "एक QCoin की कीमत क्या है?",
          "answer": "QCoin की कीमत और क्लेम के नियम कंपनी के कार्यक्रम के अनुसार अलग हो सकते हैं। जानकारी कंपनी प्रतिनिधि या काउंटर से लें।"
        },
        {
          "question": "कोड स्कैन न हो तो क्या करें?",
          "answer": "QR कोड के नीचे छपा नंबर ऐप में डालें। फिर भी दिक्कत हो तो अपने काउंटर से मदद लें।"
        },
        {
          "question": "मेरा कारोबार और कैसे जान सकता है?",
          "answer": "अपने नेटवर्क और कार्यक्रम के बारे में बात करने के लिए GatiQ Rewards टीम को कॉल या WhatsApp करें।"
        }
      ]
    },
    "closing": {
      "eyebrow": "अगला कदम लें",
      "titleFirst": "इनाम के सफ़र को",
      "titleSecond": "और आसान बनाएँ।",
      "description": "कारीगर: अपने कार्यक्रम की जानकारी कंपनी से लें। कंपनियाँ: अपना नेटवर्क जोड़ने के लिए हमसे बात करें।",
      "call": "कॉल करें",
      "whatsapp": "WhatsApp करें",
      "note": "GatiQ Rewards · कंपनी, काउंटर और कारीगरों के लिए इनाम का सफ़र"
    },
    "footer": {
      "tagline": "आपके पूरे नेटवर्क के लिए, इनाम का एक सफ़र।",
      "backToTop": "ऊपर जाएँ"
    }
  },
} as const;

export type LandingLanguage = keyof typeof translations;

const platformScreens: Record<LandingLanguage, Array<{ image: string; title: string; alt: string; description: string }>> = {
  en: [
    { image: "zenith-admin-overview.png", title: "Admin overview", alt: "Zenith Pro Adhesives Admin overview dashboard", description: "The company team sees karigars, counters, approvals, and reward-code activity in one view." },
    { image: "zenith-reward-batches.png", title: "Reward code batches", alt: "Zenith Pro Adhesives reward code batch tracker", description: "Follow each batch from issued codes through scans and remaining codes." },
    { image: "zenith-performance-tracker.png", title: "Performance tracker", alt: "Zenith Pro Adhesives performance tracker", description: "Compare scan and QCoin activity across products and counters." },
    { image: "zenith-document-upload.png", title: "Upload product information", alt: "Zenith Pro Adhesives document upload dialog with PDF, Image, and Link options", description: "Publish product information as a PDF, image, or link for the network." },
    { image: "zenith-karigar-documents.png", title: "Karigar product documents", alt: "Zenith Club Karigar app showing the product documents list", description: "Karigars can open shared product catalogues and programme information from the app." },
  ],
  hi: [
    { image: "zenith-admin-overview.png", title: "कंपनी डैशबोर्ड", alt: "Zenith Pro Adhesives कंपनी का Admin डैशबोर्ड", description: "कंपनी टीम कारीगर, काउंटर, मंज़ूरी और रिवॉर्ड कोड गतिविधि एक जगह देखती है।" },
    { image: "zenith-reward-batches.png", title: "रिवॉर्ड कोड बैच", alt: "Zenith Pro Adhesives रिवॉर्ड कोड बैच ट्रैकर", description: "हर बैच के जारी कोड, स्कैन और बाकी कोड की स्थिति देखें।" },
    { image: "zenith-performance-tracker.png", title: "परफॉर्मेंस ट्रैकर", alt: "Zenith Pro Adhesives परफॉर्मेंस ट्रैकर", description: "प्रोडक्ट और काउंटर के स्कैन और QCoin गतिविधि की तुलना करें।" },
    { image: "zenith-document-upload.png", title: "प्रोडक्ट जानकारी अपलोड", alt: "PDF, Image और Link विकल्पों वाला Zenith Pro Adhesives दस्तावेज़ अपलोड फ़ॉर्म", description: "नेटवर्क के लिए प्रोडक्ट जानकारी PDF, इमेज या लिंक के रूप में प्रकाशित करें।" },
    { image: "zenith-karigar-documents.png", title: "कारीगर के प्रोडक्ट दस्तावेज़", alt: "Zenith Club कारीगर ऐप में प्रोडक्ट दस्तावेज़ों की सूची", description: "कारीगर ऐप से साझा की गई प्रोडक्ट कैटलॉग और कार्यक्रम की जानकारी खोल सकते हैं।" },
  ],
};

export function RewardsLandingPage() {
  const { lang: language } = useLanguage("en");
  const [activeFeature, setActiveFeature] = useState(0);
  const t = translations[language];
  const screens = platformScreens[language];
  const feature = screens[activeFeature]!;
  const whatsappMessage = language === "en"
    ? "Hello, please share more information about GatiQ Rewards."
    : "Namaste, GatiQ Rewards ke baare mein jaankari chahiye.";
  const whatsappUrl = "https://wa.me/919351166339?text=" + encodeURIComponent(whatsappMessage);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t.pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [language, t.pageTitle]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="gq-landing" data-scrollable-page data-language={language}>
      <a className="gq-lp-skip" href="#gq-main">{t.skip}</a>
      <RewardsSiteHeader activePath="/" />

      <main id="gq-main">
        <section className="gq-lp-hero" id="gq-top" aria-labelledby="gq-hero-title">
          <div className="gq-lp-hero-glow" aria-hidden="true" />
          <div className="gq-lp-wrap gq-lp-hero-grid">
            <div className="gq-lp-hero-copy">
              <p className="gq-lp-eyebrow"><span />{t.hero.eyebrow}</p>
              <h1 id="gq-hero-title">{t.hero.titleFirst}<br /><em>{t.hero.titleSecond}</em></h1>
              <p className="gq-lp-hero-lede">{t.hero.description}</p>
              <div className="gq-lp-actions">
                <Link className="gq-lp-button gq-lp-button-primary" to="/for-karigars">{t.hero.journeyButton} <ArrowRight size={15} /></Link>
                <a className="gq-lp-button gq-lp-button-quiet" href={whatsappUrl} target="_blank" rel="noreferrer">{t.hero.whatsappButton} <ArrowUpRight size={15} /></a>
              </div>
              <div className="gq-lp-hero-proof">
                <span><Check size={13} /> {t.hero.proofOne}</span>
                <span><Check size={13} /> {t.hero.proofTwo}</span>
              </div>
            </div>

            <div className="gq-lp-hero-visual">
              <div className="gq-lp-visual-halo" aria-hidden="true" />
              <div className="gq-lp-device-gallery">
                <figure className="gq-lp-device gq-lp-device-desktop">
                  <div className="gq-lp-dashboard-bar">
                    <span className="gq-lp-window-dots" aria-hidden="true"><i /><i /><i /></span>
                    <span>{language === "en" ? "Desktop · Zenith Pro Adhesives" : "डेस्कटॉप · Zenith Pro Adhesives"}</span>
                    <span className="gq-lp-demo-label">{t.hero.demoWorkspace}</span>
                  </div>
                  <img src={ASSET_ROOT + "/zenith-admin-overview.png"} alt={language === "en" ? "Zenith Pro Adhesives Admin overview dashboard" : "Zenith Pro Adhesives Admin डैशबोर्ड"} />
                  <figcaption><span className="gq-lp-status-dot" />{language === "en" ? "Zenith Pro Adhesives · Admin overview" : "Zenith Pro Adhesives · Admin overview"}</figcaption>
                </figure>
                <figure className="gq-lp-device gq-lp-device-tablet">
                  <figcaption>{t.hero.tabletLabel}</figcaption>
                  <img src={ASSET_ROOT + "/zenith-performance-tablet.png"} alt={language === "en" ? "Zenith Pro Adhesives performance tracker on a tablet" : "टैबलेट पर Zenith Pro Adhesives परफॉर्मेंस ट्रैकर"} />
                </figure>
                <figure className="gq-lp-device gq-lp-device-mobile">
                  <figcaption>{t.hero.mobileLabel}</figcaption>
                  <img src={ASSET_ROOT + "/zenith-karigar-home.png"} alt={language === "en" ? "Zenith Club Karigar app home with QCoin balance and scan action" : "QCoin बैलेंस और स्कैन विकल्प वाली Zenith Club कारीगर ऐप होम स्क्रीन"} />
                </figure>
              </div>
            </div>
          </div>
          <div className="gq-lp-hero-bottom gq-lp-wrap"><span>GatiQ Rewards</span><span>{t.hero.footerLine}</span><a href="#gq-network" aria-label={t.hero.continue}><ArrowDown size={17} /></a></div>
        </section>

        <section className="gq-lp-audience" aria-labelledby="gq-audience-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-audience-heading">
              <p className="gq-lp-eyebrow is-orange">{language === "en" ? "Made for every part of the network" : "पूरे नेटवर्क के हर व्यक्ति के लिए"}</p>
              <h2 id="gq-audience-title">{language === "en" ? "Where would you like to start?" : "आप कहाँ से शुरुआत करना चाहते हैं?"}</h2>
            </div>
            <div className="gq-lp-audience-grid">
              <Link className="gq-lp-audience-card" to="/for-karigars">
                <span className="gq-lp-audience-icon is-orange"><Users size={20} /></span>
                <span className="gq-lp-audience-text"><b>{language === "en" ? "I am a Karigar" : "मैं कारीगर हूँ"}</b><small>{language === "en" ? "Learn how to scan, see QCoin, and follow your reward claim." : "स्कैन करना, QCoin देखना और इनाम क्लेम की जानकारी पाना सीखें।"}</small></span>
                <ArrowRight size={18} />
              </Link>
              <Link className="gq-lp-audience-card" to="/for-companies">
                <span className="gq-lp-audience-icon is-sand"><Factory size={20} /></span>
                <span className="gq-lp-audience-text"><b>{language === "en" ? "I represent a Company" : "मैं कंपनी से हूँ"}</b><small>{language === "en" ? "Explore code issuing, network activity, and product performance." : "कोड जारी करना, नेटवर्क गतिविधि और प्रोडक्ट प्रदर्शन देखें।"}</small></span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-network" id="gq-network" aria-labelledby="gq-network-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-section-intro is-centered">
              <p className="gq-lp-eyebrow is-orange">{t.network.eyebrow}</p>
              <h2 id="gq-network-title">{t.network.titleFirst}<br /><span>{t.network.titleSecond}</span></h2>
              <p>{t.network.description}</p>
            </div>
            <div className="gq-lp-network-flow" aria-label={t.network.aria}>
              {t.network.roles.map((role, index) => {
                const RoleIcon = roleIcons[index]!;
                return (
                  <Fragment key={role.title}>
                    <article className="gq-lp-network-node">
                      <span className={"gq-lp-node-mark " + ["is-orange", "is-sand", "is-green", "is-rose"][index]}><RoleIcon size={18} /></span>
                      <div><small>{role.meta}</small><h3>{role.title}</h3><p>{role.description}</p></div>
                    </article>
                    {index < t.network.roles.length - 1 ? <span className="gq-lp-network-link" aria-hidden="true"><ArrowRight size={15} /></span> : null}
                  </Fragment>
                );
              })}
            </div>
            <p className="gq-lp-network-note"><Sparkles size={14} aria-hidden="true" />{t.network.note}</p>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-journey" id="gq-journey" aria-labelledby="gq-journey-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-section-heading-row">
              <div className="gq-lp-section-intro">
                <p className="gq-lp-eyebrow is-orange">{t.journey.eyebrow}</p>
                <h2 id="gq-journey-title">{t.journey.titleFirst}<br /><span>{t.journey.titleSecond}</span></h2>
              </div>
              <p className="gq-lp-section-side-note">{t.journey.sideNote}</p>
            </div>
            <div className="gq-lp-journey-grid">
              {t.journey.steps.map((step, index) => {
                const StepIcon = journeyIcons[index]!;
                return (
                  <article className="gq-lp-journey-card" key={step.tag}>
                    <span className="gq-lp-journey-number">{String(index + 1).padStart(2, "0")}</span>
                    <span className={"gq-lp-journey-icon " + ["is-orange", "is-green", "is-sand", "is-rose"][index]}><StepIcon size={21} /></span>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                    <span className="gq-lp-journey-tag">{step.tag}</span>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-scan-claim" id="gq-scan-claim" aria-labelledby="gq-scan-claim-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-section-heading-row">
              <div className="gq-lp-section-intro">
                <p className="gq-lp-eyebrow is-orange">{language === "en" ? "THE KARIGAR APP" : "कारीगर ऐप"}</p>
                <h2 id="gq-scan-claim-title">{language === "en" ? <>Scan a code.<br /><span>Request a claim.</span></> : <>कोड स्कैन करें।<br /><span>क्लेम रिक्वेस्ट भेजें।</span></>}</h2>
              </div>
              <p className="gq-lp-section-side-note">{language === "en" ? "See the actual Zenith Pro Adhesives demo screens for scanning a product code and entering a QCoin redemption request." : "प्रोडक्ट कोड स्कैन करने और QCoin रिडेम्पशन रिक्वेस्ट भरने के Zenith Pro Adhesives डेमो स्क्रीन देखें।"}</p>
            </div>
            <div className="gq-lp-claim-showcase">
              <div className="gq-lp-claim-steps">
                <div><span>01</span><p>{language === "en" ? "Scan a QR code or enter its printed number." : "QR कोड स्कैन करें या उस पर छपा नंबर डालें।"}</p></div>
                <div><span>02</span><p>{language === "en" ? "See QCoin appear in the Karigar app." : "कारीगर ऐप में QCoin देखें।"}</p></div>
                <div><span>03</span><p>{language === "en" ? "Choose an amount and request a claim." : "राशि चुनें और क्लेम रिक्वेस्ट भेजें।"}</p></div>
              </div>
              <figure className="gq-lp-phone-proof">
                <ScanDemoScreenshot alt="Zenith Club Karigar app scan screen with illustrative sample QR and scan animation" />
                <figcaption>{language === "en" ? "Karigar · Scan a code" : "कारीगर · कोड स्कैन"}</figcaption>
              </figure>
              <figure className="gq-lp-phone-proof">
                <img src={ASSET_ROOT + "/zenith-karigar-redemption.png"} alt="Zenith Club Karigar app QCoin redemption request screen" loading="lazy" />
                <figcaption>{language === "en" ? "Karigar · Request a claim" : "कारीगर · क्लेम रिक्वेस्ट"}</figcaption>
              </figure>
            </div>
            <p className="gq-lp-demo-disclosure">{language === "en" ? "Screenshots are from the Zenith Pro Adhesives demo workspace." : "स्क्रीनशॉट Zenith Pro Adhesives डेमो वर्कस्पेस से हैं।"}</p>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-proof" aria-labelledby="gq-lp-proof-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-section-intro is-centered">
              <p className="gq-lp-eyebrow is-orange">{language === "en" ? "CODE TO PRODUCT INFORMATION" : "कोड से प्रोडक्ट जानकारी तक"}</p>
              <h2 id="gq-lp-proof-title">{language === "en" ? <>Track batches and<br /><span>share useful details.</span></> : <>बैच ट्रैक करें और<br /><span>काम की जानकारी साझा करें।</span></>}</h2>
              <p>{language === "en" ? "Use one view to follow reward codes, publish current product documents, and help karigars find them in the app." : "रिवॉर्ड कोड ट्रैक करें, प्रोडक्ट दस्तावेज़ प्रकाशित करें और कारीगरों को ऐप में जानकारी ढूँढने दें।"}</p>
            </div>
            <div className="gq-lp-proof-grid">
              <figure className="gq-lp-proof-card is-batches">
                <div className="gq-lp-proof-card-heading"><b>{language === "en" ? "Admin · Reward code batches" : "Admin · रिवॉर्ड कोड बैच"}</b><span>ZENITH PRO ADHESIVES</span></div>
                <img src={ASSET_ROOT + "/zenith-reward-batches.png"} alt="Zenith Pro Adhesives Admin reward batch screen with scan and outstanding counts" loading="lazy" />
              </figure>
              <figure className="gq-lp-proof-card is-upload">
                <div className="gq-lp-proof-card-heading"><b>{language === "en" ? "Admin · Add a document" : "Admin · दस्तावेज़ जोड़ें"}</b><span>PDF · IMAGE · LINK</span></div>
                <img src={ASSET_ROOT + "/zenith-document-upload.png"} alt="Zenith Pro Adhesives document upload dialog with PDF, Image, and Link options" loading="lazy" />
              </figure>
              <figure className="gq-lp-proof-card is-karigar-docs">
                <div className="gq-lp-proof-card-heading"><b>{language === "en" ? "Karigar · Product documents" : "कारीगर · प्रोडक्ट दस्तावेज़"}</b><span>ZENITH CLUB</span></div>
                <img src={ASSET_ROOT + "/zenith-karigar-documents.png"} alt="Zenith Club Karigar app document list with the product catalogue" loading="lazy" />
              </figure>
            </div>
            <p className="gq-lp-demo-disclosure">{language === "en" ? "The Karigar screenshot shows the product catalogue entry. The captured flow opens the published catalogue from the app." : "कारीगर स्क्रीनशॉट में प्रोडक्ट कैटलॉग दिखता है, जिसे ऐप से खोला जा सकता है।"}</p>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-connected" aria-labelledby="gq-lp-connected-title">
          <div className="gq-lp-wrap">
            <div className="gq-lp-section-intro is-centered">
              <p className="gq-lp-eyebrow is-orange">{language === "en" ? "ONE CONNECTED REWARD NETWORK" : "एक जुड़ा हुआ रिवॉर्ड नेटवर्क"}</p>
              <h2 id="gq-lp-connected-title">{language === "en" ? <>Admin, counter, and karigar<br /><span>work in one system.</span></> : <>Admin, काउंटर और कारीगर<br /><span>एक सिस्टम से जुड़े हैं।</span></>}</h2>
              <p>{language === "en" ? "The company dashboard, counter workspace, and Karigar app connect through GatiQ Rewards." : "कंपनी डैशबोर्ड, काउंटर वर्कस्पेस और कारीगर ऐप GatiQ Rewards से जुड़ते हैं।"}</p>
            </div>
            <div className="gq-lp-role-screens">
              <figure className="gq-lp-role-screen">
                <img src={ASSET_ROOT + "/zenith-admin-overview.png"} alt="Zenith Pro Adhesives Admin dashboard" loading="lazy" />
                <figcaption>{language === "en" ? "Admin dashboard" : "Admin डैशबोर्ड"}</figcaption>
              </figure>
              <figure className="gq-lp-role-screen">
                <img src={ASSET_ROOT + "/zenith-counter-dashboard.png"} alt="Zenith Pro Adhesives counter dashboard" loading="lazy" />
                <figcaption>{language === "en" ? "Counter dashboard" : "काउंटर डैशबोर्ड"}</figcaption>
              </figure>
              <figure className="gq-lp-role-screen is-mobile">
                <img src={ASSET_ROOT + "/zenith-karigar-home.png"} alt="Zenith Club Karigar mobile app home screen" loading="lazy" />
                <figcaption>{language === "en" ? "Karigar mobile app" : "कारीगर मोबाइल ऐप"}</figcaption>
              </figure>
            </div>
            <div className="gq-lp-role-linkage">
              <svg viewBox="0 0 1000 155" role="img" aria-label={language === "en" ? "Admin, counter, and Karigar connect through GatiQ Rewards" : "Admin, काउंटर और कारीगर GatiQ Rewards से जुड़े हैं"}>
                <defs><marker id="gq-lp-link-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0 0 L8 4 L0 8 Z" fill="#c54e1b" /></marker></defs>
                <path className="gq-lp-link-path" d="M165 0 C165 58 360 74 500 118" markerEnd="url(#gq-lp-link-arrow)" />
                <path className="gq-lp-link-path" d="M500 0 L500 118" markerEnd="url(#gq-lp-link-arrow)" />
                <path className="gq-lp-link-path" d="M835 0 C835 58 640 74 500 118" markerEnd="url(#gq-lp-link-arrow)" />
              </svg>
              <div className="gq-lp-role-hub"><img src={ASSET_ROOT + "/gatiq-logo-transparent.png"} alt="" /><b>GatiQ Rewards</b><span>{language === "en" ? "One connected platform" : "एक जुड़ा हुआ प्लेटफ़ॉर्म"}</span></div>
            </div>
            <p className="gq-lp-demo-disclosure">{language === "en" ? "Screenshots show the Zenith Pro Adhesives demo organization and its Karigar workspace." : "स्क्रीनशॉट Zenith Pro Adhesives डेमो संगठन और उसके कारीगर वर्कस्पेस के हैं।"}</p>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-platform" id="gq-platform" aria-labelledby="gq-platform-title">
          <div className="gq-lp-wrap gq-lp-platform-grid">
            <div className="gq-lp-platform-copy">
              <p className="gq-lp-eyebrow is-orange">{t.platform.eyebrow}</p>
              <h2 id="gq-platform-title">{t.platform.titleFirst}<br /><span>{t.platform.titleSecond}</span></h2>
              <p>{t.platform.description}</p>
              <div className="gq-lp-feature-tabs" role="group" aria-label={t.platform.featureGroup}>
                {screens.map((item, index) => (
                  <button
                    className={index === activeFeature ? "gq-lp-feature-tab is-active" : "gq-lp-feature-tab"}
                    type="button"
                    key={item.title}
                    aria-pressed={index === activeFeature}
                    onClick={() => setActiveFeature(index)}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>{item.title}
                  </button>
                ))}
              </div>
              <p className="gq-lp-feature-description" aria-live="polite">{feature.description}</p>
            </div>
            <figure className="gq-lp-feature-screen">
              <div className="gq-lp-screen-topline"><span />GatiQ Rewards <small>{t.platform.screenLabel}</small></div>
              <img key={feature.image} src={ASSET_ROOT + "/" + feature.image} alt={feature.alt} />
              <figcaption><span>{t.platform.actualUi}</span><span>{feature.title}</span></figcaption>
            </figure>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-film" id="gq-film" aria-labelledby="gq-film-title">
          <div className="gq-lp-wrap gq-lp-film-grid">
            <div className="gq-lp-film-copy">
              <p className="gq-lp-eyebrow is-orange">{t.film.eyebrow}</p>
              <h2 id="gq-film-title">{t.film.titleFirst}<br /><span>{t.film.titleSecond}</span></h2>
              <p>{t.film.description}</p>
              <div className="gq-lp-film-meta"><span><Play size={14} aria-hidden="true" /></span><span><b>{t.film.title}</b><small>{t.film.detail}</small></span></div>
            </div>
            <div className="gq-lp-film-frame">
              <video controls playsInline preload="metadata" poster={ASSET_ROOT + "/zenith-admin-overview.png"} aria-label={t.film.videoAlt}>
                <source src={ASSET_ROOT + "/gatiq-rewards-product-film.mp4"} type="video/mp4" />
              </video>
              <p>{t.film.disclosure}</p>
            </div>
          </div>
        </section>

        <section className="gq-lp-clarity" aria-labelledby="gq-clarity-title">
          <div className="gq-lp-wrap gq-lp-clarity-grid">
            <span className="gq-lp-clarity-mark" aria-hidden="true">G</span>
            <div><p className="gq-lp-eyebrow is-orange">{t.clarity.eyebrow}</p><h2 id="gq-clarity-title">{t.clarity.title}</h2><p>{t.clarity.description}</p></div>
            <a href="#gq-faq" aria-label={t.clarity.faqLink}><ArrowDown size={16} /></a>
          </div>
        </section>

        <section className="gq-lp-section gq-lp-faq" id="gq-faq" aria-labelledby="gq-faq-title">
          <div className="gq-lp-wrap gq-lp-faq-grid">
            <div className="gq-lp-faq-heading"><p className="gq-lp-eyebrow is-orange">{t.faq.eyebrow}</p><h2 id="gq-faq-title">{t.faq.title}</h2><p>{t.faq.intro}</p><Link to="/help">{language === "en" ? "Browse all help answers" : "सभी सवालों के जवाब देखें"} <ArrowRight size={14} /></Link><a href="tel:+919351166339">{t.faq.call} <ArrowUpRight size={14} /></a></div>
            <div className="gq-lp-faq-list">
              {t.faq.items.map((item) => <details key={item.question}><summary>{item.question}<ChevronDown size={16} /></summary><p>{item.answer}</p></details>)}
            </div>
          </div>
        </section>

        <section className="gq-lp-closing" id="gq-contact" aria-labelledby="gq-closing-title">
          <div className="gq-lp-closing-glow" aria-hidden="true" />
          <div className="gq-lp-wrap gq-lp-closing-content">
            <RewardsBrandLockup className="gq-lp-closing-brand" />
            <p className="gq-lp-eyebrow">{t.closing.eyebrow}</p>
            <h2 id="gq-closing-title">{t.closing.titleFirst}<br /><em>{t.closing.titleSecond}</em></h2>
            <p>{t.closing.description}</p>
            <div className="gq-lp-closing-actions"><a className="gq-lp-button gq-lp-button-primary" href="tel:+919351166339">{t.closing.call} <ArrowUpRight size={15} /></a><a className="gq-lp-button gq-lp-button-whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer">{t.closing.whatsapp} <ArrowUpRight size={15} /></a></div>
            <a className="gq-lp-phone" href="tel:+919351166339">+91 93511 66339</a>
          </div>
          <RewardsSiteLinks className="gq-lp-closing-links" />
          <p className="gq-lp-closing-note">{t.closing.note}</p>
        </section>
      </main>

      <RewardsSiteFooter showLinks={false} />
    </div>
  );
}
